import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const TYPE_ICON_MAP = {
  TRADE_FILL: { icon: 'TrendingUp', color: 'text-emerald-400' },
  FACTION_ANNOUNCEMENT: { icon: 'Users', color: 'text-blue-400' },
  CRIME_COMPLETE: { icon: 'Skull', color: 'text-orange-400' },
  BUSINESS_REVENUE: { icon: 'Building2', color: 'text-yellow-400' },
  WAR_DECLARED: { icon: 'Swords', color: 'text-red-400' },
  LEVEL_UP: { icon: 'TrendingUp', color: 'text-purple-400' },
  ACHIEVEMENT_UNLOCKED: { icon: 'Trophy', color: 'text-yellow-400' },
  PRICE_ALERT: { icon: 'Bell', color: 'text-yellow-400' },
  SYSTEM: { icon: 'Settings', color: 'text-muted-foreground' },
};

const CATEGORY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'trades', label: 'Trades', types: ['TRADE_FILL'] },
  { id: 'faction', label: 'Faction', types: ['FACTION_ANNOUNCEMENT', 'WAR_DECLARED'] },
  { id: 'crimes', label: 'Crimes', types: ['CRIME_COMPLETE'] },
  { id: 'business', label: 'Business', types: ['BUSINESS_REVENUE'] },
  { id: 'system', label: 'System', types: ['LEVEL_UP', 'ACHIEVEMENT_UNLOCKED', 'PRICE_ALERT', 'SYSTEM'] },
];

const relativeTime = (ts) => {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts)?.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ts)?.toLocaleDateString();
};

const NotificationsPage = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const unreadCount = notifications?.filter(n => !n?.read_at)?.length;

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(100);
      if (!error && data) setNotifications(data);
    } catch (e) {
      console.error('[Notifications] Load error:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Real-time subscription
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase?.channel(`notifications-page:${user?.id}`)?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user?.id}`,
      }, (payload) => {
        if (payload?.new) setNotifications(prev => [payload?.new, ...prev]);
      })?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [user?.id]);

  const markAllRead = useCallback(async () => {
    const now = new Date()?.toISOString();
    setNotifications(prev => prev?.map(n => ({ ...n, read_at: n?.read_at || now })));
    try {
      if (user?.id) await supabase?.rpc('mark_all_notifications_read', { p_user_id: user?.id });
    } catch {}
  }, [user?.id]);

  const markRead = useCallback(async (id) => {
    setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, read_at: new Date()?.toISOString() } : n));
    try {
      await supabase?.from('notifications')?.update({ read_at: new Date()?.toISOString() })?.eq('id', id);
    } catch {}
  }, []);

  const clearAll = useCallback(async () => {
    setNotifications([]);
    try {
      if (user?.id) await supabase?.from('notifications')?.delete()?.eq('user_id', user?.id);
    } catch {}
  }, [user?.id]);

  const filtered = notifications?.filter(n => {
    if (filter === 'all') return true;
    const cat = CATEGORY_FILTERS?.find(c => c?.id === filter);
    return cat?.types?.includes(n?.notification_type);
  });

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(p => !p)}
        notificationCounts={{ notifications: unreadCount }}
      />
      <main className="flex-1 overflow-y-auto" style={{ marginLeft: sidebarCollapsed ? '72px' : '280px' }}>
        <div className="p-6 max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-h2 font-heading text-foreground flex items-center gap-3">
                <Icon name="Bell" size={28} color="var(--color-primary)" />
                Notifications
                {unreadCount > 0 && (
                  <span className="text-sm px-2 py-0.5 rounded-full bg-primary text-zinc-950 font-bold">{unreadCount}</span>
                )}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-primary hover:text-primary/80 transition-colors">
                  Mark all as read
                </button>
              )}
              {notifications?.length > 0 && (
                <button onClick={clearAll} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {CATEGORY_FILTERS?.map(f => (
              <button
                key={f?.id}
                onClick={() => setFilter(f?.id)}
                className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-all ${
                  filter === f?.id
                    ? 'bg-primary text-zinc-950' :'bg-surface text-muted-foreground border border-border hover:border-primary/30'
                }`}
              >
                {f?.label}
              </button>
            ))}
          </div>

          {/* Notifications List */}
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)]?.map((_, i) => (
                <div key={i} className="card p-4 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-surface" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-surface rounded w-1/3" />
                      <div className="h-2 bg-surface rounded w-2/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filtered?.length === 0 ? (
            <div className="card p-12 text-center">
              <Icon name="Bell" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No notifications</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered?.map((notif) => {
                const iconCfg = TYPE_ICON_MAP?.[notif?.notification_type] || TYPE_ICON_MAP?.SYSTEM;
                return (
                  <div
                    key={notif?.id}
                    onClick={() => markRead(notif?.id)}
                    className={`card p-4 cursor-pointer transition-all hover:shadow-card-hover ${
                      !notif?.read_at ? 'border-primary/20' : ''
                    }`}
                    style={!notif?.read_at ? { background: 'rgba(124,58,237,0.03)' } : {}}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-surface border border-border flex items-center justify-center flex-shrink-0">
                        <Icon name={iconCfg?.icon} size={16} className={iconCfg?.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-semibold ${!notif?.read_at ? 'text-foreground' : 'text-muted-foreground'}`}>
                            {notif?.title}
                          </p>
                          <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">{relativeTime(notif?.created_at)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{notif?.message}</p>
                        {notif?.notification_type && (
                          <span className="text-[10px] text-muted-foreground/50 mt-1 inline-block capitalize">
                            {notif?.notification_type?.toLowerCase()?.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      {!notif?.read_at && (
                        <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
